import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Loader2 } from 'lucide-react';
import { clientsApi, type PatientSearchResult } from '@/features/clients/clients.api';
import { useT } from '@/i18n';
import { cn } from '@/lib/utils';

/**
 * Global patient search — type-ahead in the app header, navigates to
 * /clients/:id on selection.
 *
 * Resolves Bug-User-01 (no patient search) and Bug-User-05 (callbacks
 * can't find the caller). Visible to agent/supervisor/admin. Uploader
 * never sees this component (filtered in Header).
 *
 * Keyboard: '/' focuses the input from anywhere, ESC clears, ArrowDown /
 * ArrowUp navigate results, Enter selects. The backend audit-logs each
 * profile open as VIEW_CLIENT.
 */
const MIN_QUERY_LEN = 3;

/**
 * F5-R8a — variante `bar`: la MISMA búsqueda viva (debounce 400ms, abort,
 * atajo «/», flechas + Enter, audit VIEW_CLIENT del server) vestida al dibujo
 * «Agente v2 — Header» para vivir DENTRO de la barra: flex 0 1 320 / mín 220,
 * alto 44, fondo #fafbfc, borde 1.5 nav-card radio 10, ícono 16 #9aa7b2,
 * chip «/» de 20×20. `placeholder`/`ariaLabel` los pone el caller (el dibujo
 * dice qué acepta: «ID de Medicaid o teléfono completo»). El default queda
 * EXACTAMENTE igual para los demás roles.
 */
export function PatientSearchBar({
  variant = 'default',
  placeholder,
  ariaLabel,
  className,
  autoFocus = false,
}: {
  variant?: 'default' | 'bar';
  placeholder?: string;
  ariaLabel?: string;
  className?: string;
  /** F5-R11: la lupa del header angosto abre la barra ya enfocada. */
  autoFocus?: boolean;
} = {}): JSX.Element {
  const t = useT();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [results, setResults] = useState<PatientSearchResult[] | null>(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);

  // 400ms debounce. Was 250ms, raised on 2026-08-03: at 474,960 clients each
  // fired request is expensive enough that a burst of keystrokes is worth
  // collapsing harder.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query.trim()), 400);
    return () => clearTimeout(timer);
  }, [query]);

  // Trigger search when debounced query changes.
  //
  // 2026-08-03: the old cleanup only flipped a `cancelled` flag, which drops
  // the RESPONSE but leaves the request running server-side. With the old
  // full-scan query that meant one abandoned 60s decrypt scan per keystroke —
  // three were observed firing inside the same second. AbortController makes
  // the cancellation real, so a superseded query stops costing anything.
  useEffect(() => {
    if (debouncedQuery.length < MIN_QUERY_LEN) {
      setResults(null);
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    clientsApi
      .searchPatients(debouncedQuery, 20, controller.signal)
      .then((rows) => {
        if (controller.signal.aborted) return;
        setResults(rows);
        setActiveIdx(0);
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setResults([]);
      })
      .finally(() => {
        if (controller.signal.aborted) return;
        setLoading(false);
      });
    return () => controller.abort();
  }, [debouncedQuery]);

  // Global '/' shortcut to focus the input (skipped when already in a field).
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key !== '/') return;
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
        return;
      }
      e.preventDefault();
      inputRef.current?.focus();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Click outside closes dropdown.
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelect = (clientId: string) => {
    setQuery('');
    setResults(null);
    setOpen(false);
    navigate(`/clients/${clientId}`);
  };

  const handleKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setQuery('');
      setOpen(false);
      inputRef.current?.blur();
      return;
    }
    if (!results || results.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(results.length - 1, i + 1));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      const picked = results[activeIdx];
      if (picked) handleSelect(picked.id);
    }
  };

  // An empty result is ambiguous right now: it means either "that CIN/phone
  // is not in the system" OR "you searched by last name, which is still being
  // rebuilt". Say both rather than a bare "sin resultados", which would read
  // as "this patient does not exist" and send the agent looking elsewhere.
  const emptyNeedsExplaining = results !== null && results.length === 0 && !loading;

  const showDropdown = open && (loading || results !== null);

  const ph = placeholder ?? t('header.patientSearch.placeholder');
  const aria = ariaLabel ?? ph;

  return (
    <div
      ref={containerRef}
      // F5-R9m: en `bar` el mínimo cede con el ancho de la ventana (220 era
      // fijo y empujaba el min-content de la barra fuera del viewport en
      // pantalla dividida). El `className` deja al chrome decidir a partir de
      // qué breakpoint se muestra.
      className={
        variant === 'bar'
          // basis 150 (no 320): el wrap decide con la base, así que una base
          // grande la mandaba a la línea de abajo aunque cupiera encogida.
          // Crece hasta 320 con el sitio que sobre.
          ? cn('relative flex-[1_1_150px] max-w-[320px] min-w-[150px] lg:min-w-[220px]', className)
          : 'relative w-full'
      }
    >
      {/* F5-R1a — input del spec: borde 1.5 line, radio 8, focus teal.
          F5-R8a `bar` — searchWrapStyle del dibujo: 44px, #fafbfc, borde
          nav-card radio 10, gap 9, chip «/» siempre visible. */}
      <div
        className={
          variant === 'bar'
            ? 'flex items-center gap-[9px] h-11 px-3 bg-[#fafbfc] rounded-[10px] border-[1.5px] border-nav-card focus-within:border-nav-teal transition-colors'
            : 'flex items-center gap-2 rounded-lg border-[1.5px] border-nav-line bg-white px-2.5 py-2 focus-within:border-nav-teal focus-within:ring-2 focus-within:ring-nav-teal/30 transition-colors'
        }
      >
        <Search
          className={
            variant === 'bar' ? 'h-4 w-4 text-[#9aa7b2] shrink-0' : 'h-4 w-4 text-nav-slate shrink-0'
          }
          aria-hidden
        />
        <input
          ref={inputRef}
          autoFocus={autoFocus}
          type="search"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKey}
          placeholder={ph}
          aria-label={aria}
          className={
            variant === 'bar'
              ? 'flex-1 min-w-0 bg-transparent border-0 outline-none font-body text-[13px] text-nav-navy placeholder:text-nav-faint'
              : 'bg-transparent border-0 outline-none font-body text-sm w-full text-nav-navy placeholder:text-nav-faint'
          }
          autoComplete="off"
        />
        {loading && <Loader2 className="h-4 w-4 text-nav-teal animate-spin shrink-0" aria-hidden />}
        {variant === 'bar' ? (
          <span className="flex-none inline-flex items-center justify-center w-5 h-5 rounded-[5px] bg-nav-hair font-head text-[11px] font-bold text-[#9aa7b2]">
            /
          </span>
        ) : (
          <span className="hidden lg:inline font-head text-[10px] font-bold text-nav-faint border border-nav-card rounded px-1 leading-none py-0.5 shrink-0">/</span>
        )}
      </div>

      {showDropdown && (
        <div
          role="listbox"
          className={
            'absolute left-0 right-0 mt-1 max-h-80 overflow-y-auto rounded-[10px] border-[1.5px] border-nav-card bg-white z-30' +
            (variant === 'bar' ? ' min-w-[300px]' : '')
          }
          style={{ boxShadow: '0 8px 24px rgba(18,43,68,.12)' }}
        >
          {results === null && loading && (
            <div className="px-3 py-2 text-xs text-nav-slate">{t('header.patientSearch.loading')}</div>
          )}
          {emptyNeedsExplaining && (
            <>
              <div className="px-3 py-2 text-xs text-nav-slate">{t('header.patientSearch.noResults')}</div>
              <div className="px-3 py-2 text-xs text-nav-tealDark bg-nav-tealTint border-t border-nav-hair">
                {t('header.patientSearch.hintIndexedOnly')}
              </div>
            </>
          )}
          {results !== null && results.length > 0 && results.map((r, idx) => (
            <button
              key={r.id}
              type="button"
              role="option"
              aria-selected={idx === activeIdx}
              onMouseEnter={() => setActiveIdx(idx)}
              onClick={() => handleSelect(r.id)}
              className={
                'w-full text-left px-3 py-2 text-sm border-b border-nav-hair last:border-b-0 ' +
                (idx === activeIdx ? 'bg-nav-tealTint' : 'hover:bg-nav-softer')
              }
            >
              <div className="font-head font-semibold text-[13.5px] text-nav-navy truncate">
                {r.lastName}, {r.firstName}
              </div>
              <div className="text-[11px] text-nav-slate truncate">
                MRN ···{r.cinLast6}
                {r.phone ? ` · ${r.phone}` : ''}
                {r.dob ? ` · DOB ${r.dob}` : ''}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
